import { diffWords } from 'diff';
import { unicodes, unicodeRegex } from './constants/unicodes';

/**
 *
 * @param {string} html
 * @returns {object} Object with private use unicodes as the key and the HTML tag as the value
 */
function getHTMLHashMap(html) {
  const htmlRegex = /<[^<>]+>/g;
  const hashMap = {};
  const unicodeCopy = [...unicodes];
  const matches = html ? [...html.matchAll(htmlRegex)] : [];

  matches.forEach((match) => {
    const values = Object.values(hashMap);

    if (!values.includes(match[0])) {
      hashMap[unicodeCopy.shift()] = match[0]; // eslint-disable-line
    }
  });

  return hashMap;
}

/**
 *
 * @param {string} html
 * @param {object} hashMap
 * @returns {string} A string with all HTML tags replaced with a private use unicode character
 */
function getUnicodifiedString(html, hashMap = {}) {
  const values = Object.values(hashMap);
  const keys = Object.keys(hashMap);

  values.forEach((value, i) => {
    const regex = new RegExp(value, 'g');
    const unicodeChar = keys[i];

    html = html.replace(regex, unicodeChar); // eslint-disable-line
  });

  return html;
}

/**
 *
 * @param {string} html
 * @param {object} hashMap
 * @returns {string} Reverted string that replaced private use
 * unicode characters with the associated html tag
 */
function unicodeToHTMLString(html, hashMap = {}) {
  const keys = Object.keys(hashMap);
  const values = Object.values(hashMap);

  keys.forEach((key, i) => {
    const regex = new RegExp(key, 'g');
    const value = values[i];

    html = html.replace(regex, value); // eslint-disable-line
  });

  return html;
}

/**
 *
 * @param {string} str
 * @param {string} cssClass
 * @returns {string} A string with the valid characters wrapped in html element and proper class
 */
function formatString(str, cssClass) {
  let wasValid = true;
  const split = str.split('');
  const start = [];
  const end = [];

  split.forEach((char, index) => {
    const isValid = !unicodeRegex.test(char);

    if (index === 0 && isValid) {
      start.push(index);
      if (split.length === 1) {
        end.push(index);
      }
    } else if (index === 0 && !isValid) {
      wasValid = false;
    } else if (index === split.length - 1 && isValid) {
      end.push(index);
    } else if (index === split.length - 1 && !isValid) {
      for (let i = index - 1; i >= 0; i--) {
        const isPreviousValid = !unicodeRegex.test(split[i]);

        if (isPreviousValid) {
          end.push(i);
          break;
        }
      }
    } else if (!wasValid && isValid) {
      wasValid = true;
      start.push(index);
    } else if (wasValid && !isValid) {
      wasValid = false;
      end.push(index - 1);
    }
  });

  const newStr = split.map((char, i) => {
    if (start.includes(i) && end.includes(i)) {
      return `<span class="${cssClass}">${char}</span>`;
    }

    if (start.includes(i)) {
      return `<span class="${cssClass}">${char}`;
    }

    if (end.includes(i)) {
      return `${char}</span>`;
    }

    return char;
  });

  return newStr.join('');
}

/**
 *
 * @param {Array} diff
 * @returns {string} Formatted string with removed and added classes on diff changes
 */
function createFormattedHTML(diff) {
  let finalStr = '';

  diff.forEach((instance) => {
    if (instance.removed) {
      const newStr = formatString(instance.value, 'removed-diff');
      finalStr += newStr;
    } else if (instance.added) {
      const newStr = formatString(instance.value, 'added-diff');
      finalStr += newStr;
    } else {
      finalStr += instance.value;
    }
  });
  return finalStr;
}

/**
 *
 * @param {string} current
 * @param {string} previous
 * @returns {object} Object containing the previous revision with removed changes
 * and current revision with added changes
 * @description Takes two revision html strings,
 * creates a hashmap using unicode and the existing html tags,
 * converts the html tags to private use unicode characters,
 * runs a diff using the jsdiff diffWords function,
 * separates into added and removed diff actions,
 * inserts classes to style changes,
 * and converts back into html string
 */
export function getHTMLDiff(current, previous) {
  const hashMap = getHTMLHashMap(previous + current);

  const previousUnicodeString = getUnicodifiedString(previous, hashMap);
  const currentUnicodeString = getUnicodifiedString(current, hashMap);

  const diff = diffWords(previousUnicodeString, currentUnicodeString);

  const previousDiff = diff.filter((d) => !d.added);
  const currentDiff = diff.filter((d) => !d.removed);

  const previousStyledDiffSting = createFormattedHTML(previousDiff);
  const currentStyledDiffSting = createFormattedHTML(currentDiff);

  const previousFinalHTMLString = unicodeToHTMLString(previousStyledDiffSting, hashMap);
  const currentFinalHTMLString = unicodeToHTMLString(currentStyledDiffSting, hashMap);

  return { previous: previousFinalHTMLString, current: currentFinalHTMLString };
}

/**
 *
 * @param {string} current
 * @param {string} previous
 * @returns {string} A single HTML string with removed and added changes interleaved inline
 * @description Like getHTMLDiff but returns one combined string showing all deletions
 * (wrapped in removed-diff) and insertions (wrapped in added-diff) in their original order
 */
export function getHTMLDiffCombined(current, previous) {
  const hashMap = getHTMLHashMap(previous + current);

  const previousUnicodeString = getUnicodifiedString(previous, hashMap);
  const currentUnicodeString = getUnicodifiedString(current, hashMap);

  const diff = diffWords(previousUnicodeString, currentUnicodeString);

  const combinedStyledDiffString = createFormattedHTML(diff);

  return unicodeToHTMLString(combinedStyledDiffString, hashMap);
}

export const joinAndDiffBlocks = (oldBlocks, newBlocks) => {
  const oldHtml = (oldBlocks || []).map((block) => block.html).join('');
  const newHtml = (newBlocks || []).map((block) => block.html).join('');

  return getHTMLDiffCombined(newHtml, oldHtml);
};
